//
//
//
//

import {
  AnimationState,
  AnimationStateData,
  AssetManager,
  AtlasAttachmentLoader,
  ResizeMode,
  SceneRenderer,
  Shader,
  Skeleton,
  SkeletonBinary,
  SkeletonJson,
  Vector2
} from '@esotericsoftware/spine-webgl';

export default {
  name: 'Spine',
  props: {
    modelName: {
      type: String,
      required: true
    },
    modelNamePostfix: {
      type: String,
      default: ''
    },
    assetsPath: {
      type: String,
      required: true
    },
    animationName: {
      type: String,
      required: true
    },
    isJson: {
      type: Boolean,
      default: true
    },
    loop: {
      type: Boolean,
      default: false
    },
    staticAnimationName: {
      type: String,
      default: null
    },
    speed: {
      type: Number,
      default: 1
    },
    zoom: {
      type: Number,
      default: 1
    },
    fps: {
      type: Number,
      default: 35
    },
    largestAnimationName: {
      type: String,
      default: ''
    },
    offset: {
      type: Number,
      default: 0
    },
    premultipliedAlpha: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentSpeed: this.speed,
      currentOffset: this.offset
    };
  },
  watch: {
    animationName() {
      this.handleAnimationOrLoopChange();
    },
    loop() {
      this.handleAnimationOrLoopChange();
    },
    staticAnimationName(newVal, oldVal) {
      if ((!newVal && oldVal) || (newVal && !oldVal)) {
        if (newVal) {
          this.setSpeed(0);
        } else {
          this.setSpeed(this.currentSpeed);
        }
        this.renderFrame();
      }
    },
    speed(newVal) {
      this.currentSpeed = newVal;
      this.setSpeed(newVal);
    },
    offset(newVal) {
      this.currentOffset = newVal;
      this.then = undefined;
    }
  },
  created() {
    // 实例非响应式内部属性（避免 Vue 2 响应式系统监听 WebGL/Spine 大对象开销）
    this.trackIndex = 0;
    this.canvas = null;
    this.webglCtx = null;
    this.shader = null;
    this.skeleton = null;
    this.assetManager = null;
    this.animationState = null;
    this.sceneRenderer = null;
    this.assets = null;
    this.isPlaying = true;
    this.then = undefined;
    this.rafId = undefined;
    this.animationDuration = 0;
    this.totalAnimationFrames = 0;
    this.frameInterval = 0;
  },
  mounted() {
    this.createCanvas();
    this.initSpine();
  },
  beforeDestroy() {
    this.isPlaying = false;

    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }

    if (this.shader) {
      this.shader.unbind();
    }

    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  },
  methods: {
    /**
     * 响应动画名称或循环状态更新
     */
    handleAnimationOrLoopChange() {
      this.setAnimation(this.staticAnimationName || this.animationName, this.loop);
    },

    /**
     * 创建 Canvas 并插入容器
     */
    createCanvas() {
      const container = this.$refs.container;
      if (!container) return;

      this.canvas = document.createElement('canvas');
      this.canvas.style.width = '100%';
      this.canvas.style.height = '100%';

      this.setCanvasWidth();

      this.webglCtx =
        this.canvas.getContext('webgl') ||
        this.canvas.getContext('experimental-webgl');

      container.appendChild(this.canvas);
    },

    /**
     * 设置 Canvas 宽高像素值
     */
    setCanvasWidth() {
      const container = this.$refs.container;
      if (!container || !this.canvas) return;

      this.canvas.width = container.offsetWidth;
      this.canvas.height = container.offsetHeight;
    },

    /**
     * 初始化 Spine 实例
     */
    initSpine() {
      if (!this.webglCtx) return;

      const postfix = this.modelNamePostfix || '';
      const ext = this.isJson ? '.json' : '.skel';

      this.assets = {
        skeleton: `${this.assetsPath}${this.modelName}${postfix}${ext}`,
        atlas: `${this.assetsPath}${this.modelName}.atlas`
      };

      console.log(`[Spine] Loading assets:`, this.assets);

      try {
        this.initRenderer();
      } catch (err) {
        console.warn('[Spine] WebGL shader/renderer init failed', err);
        return;
      }

      this.loadAssets();
      this.loadSkeleton();
    },

    /**
     * 初始化 Shader 和渲染器
     */
    initRenderer() {
      this.shader = Shader.newTwoColoredTextured(this.webglCtx);
      this.assetManager = new AssetManager(this.webglCtx);
      this.sceneRenderer = new SceneRenderer(this.canvas, this.webglCtx);
    },

    /**
     * 加载骨骼文件与纹理图集
     */
    loadAssets() {
      if (this.isJson) {
        this.assetManager.loadText(this.assets.skeleton);
      } else {
        this.assetManager.loadBinary(this.assets.skeleton);
      }

      this.assetManager.loadTextureAtlas(this.assets.atlas);
    },

    /**
     * 轮询检查资源是否加载完成
     */
    loadSkeleton() {
      if (this.assetManager.isLoadingComplete()) {
        this.doLoadSkeleton();
        if (this.skeleton) {
          this.render();
        }
        return;
      }

      window.requestAnimationFrame(() => this.loadSkeleton());
    },

    /**
     * 解析骨骼数据生成 Skeleton 实例
     */
    doLoadSkeleton() {
      const atlasText = this.assetManager.get(this.assets.atlas);
      const atlasLoader = new AtlasAttachmentLoader(atlasText);
      const skeletonParser = this.isJson
        ? new SkeletonJson(atlasLoader)
        : new SkeletonBinary(atlasLoader);

      skeletonParser.scale = 1;

      const rawSkeletonData = this.assetManager.get(this.assets.skeleton);
      if (rawSkeletonData) {
        const skeletonData = skeletonParser.readSkeletonData(rawSkeletonData);
        this.skeleton = new Skeleton(skeletonData);
      }
    },

    /**
     * 设置 Spine 状态与事件回调
     */
    setupSpineListeners() {
      if (!this.skeleton || !this.skeleton.data) return;

      this.animationState = new AnimationState(new AnimationStateData(this.skeleton.data));

      this.animationState.addListener({
        start: (trackEntry) => {
          if (trackEntry.trackIndex === this.trackIndex) {
            const animName = trackEntry.animation ? trackEntry.animation.name : '';
            this.$emit('animation-start', animName);
          }
        },
        complete: (trackEntry) => {
          if (trackEntry.trackIndex === this.trackIndex) {
            const animName = trackEntry.animation ? trackEntry.animation.name : '';

            if (trackEntry.loop) {
              this.$emit('animation-loop-complete', animName);
            } else {
              this.$emit('animation-complete', animName);
            }
          }
        }
      });
    },

    /**
     * 首次加载完成后的核心渲染流程
     */
    render() {
      if (!this.skeleton || !this.skeleton.data) return;

      const animationName = this.staticAnimationName || this.animationName;

      this.setupSpineListeners();
      this.setAnimation(animationName, this.loop);

      this.$emit('model-loaded');

      this.setSpeed(this.currentSpeed);

      if (this.largestAnimationName) {
        this.animationState.setAnimation(this.trackIndex, this.largestAnimationName, true);
      }

      this.resize();

      if (this.largestAnimationName) {
        this.animationState.setAnimation(this.trackIndex, animationName, this.loop);
      }

      this.renderFrame();
    },

    /**
     * 设置当前动画并计算帧率参数
     */
    setAnimation(animationName, loop) {

      console.log('animationName', animationName, 'loop', loop);
      console.log('this.animationState', this.animationState)
      console.log('this.skeleton', this.skeleton)
      if (!this.animationState || !this.skeleton) return;

      const animation = this.skeleton.data.findAnimation(animationName);
      if (!animation)
      { 
        console.warn(`[Spine] Animation "${animationName}" 动画没有找到 "${this.modelName}"`);
        return;
      } 

      this.animationDuration = animation.duration;
      this.totalAnimationFrames = animation.duration * this.fps;
      this.frameInterval = (this.totalAnimationFrames / this.animationDuration) / 1000;

      this.animationState.setAnimation(this.trackIndex, animationName, loop);
      this.$emit('animation-change', animationName);
    },

    /**
     * 设置播放速度
     */
    setSpeed(speed) {
      if (!this.animationState) return;
      this.animationState.timeScale = speed;
    },

    /**
     * 调整渲染尺寸与相机视角 (可供父组件 ResizeObserver 手动调用)
     */
    resize(newWidth, newHeight) {
      if (!this.animationState || !this.skeleton || !this.sceneRenderer || !this.canvas) {
        return;
      }

      const container = this.$refs.container;
      if (container) {
        if (newWidth) container.style.width = newWidth;
        if (newHeight) container.style.height = newHeight;
      }

      const oldCanvasWidth = this.canvas.width;
      const oldCanvasHeight = this.canvas.height;

      this.setCanvasWidth();

      const sizeChanged = oldCanvasWidth !== this.canvas.width || oldCanvasHeight !== this.canvas.height;

      this.animationState.update(0);
      this.animationState.apply(this.skeleton);

      this.skeleton.updateWorldTransform();

      const offset = new Vector2();
      const size = new Vector2();

      this.skeleton.getBounds(offset, size);
      this.sceneRenderer.resize(ResizeMode.Expand);

      const { camera } = this.sceneRenderer;
      const zoom = size.x > size.y ? size.x / this.canvas.width : size.y / this.canvas.height;

      camera.position.x = offset.x + size.x / 2;
      camera.position.y = offset.y + size.y / 2;
      camera.zoom = zoom / (this.zoom || 1);

      if (sizeChanged) {
        console.log(`[${this.modelName}] Canvas: ${oldCanvasWidth}x${oldCanvasHeight} → ${this.canvas.width}x${this.canvas.height}`);
      }

      camera.update();
    },

    /**
     * 逐帧渲染与动画循环
     */
    renderFrame() {
      if (!this.isPlaying) return;

      if (!this.then) {
        let now = Date.now();

        if (this.currentOffset > 0 && this.currentOffset <= 1) {
          now -= this.animationDuration * 1000 * this.currentOffset;
        }

        this.then = now;
      }

      const now = Date.now();
      const delta = now - this.then;

      if (delta > this.frameInterval) {
        this.then = now - (delta % this.frameInterval);

        this.animationState.update(delta / 1000);
        this.animationState.apply(this.skeleton);
        this.skeleton.updateWorldTransform();

        this.sceneRenderer.resize(ResizeMode.Expand);

        this.webglCtx.clearColor(0, 0, 0, 0);
        this.webglCtx.clear(this.webglCtx.COLOR_BUFFER_BIT);

        this.sceneRenderer.begin();
        this.sceneRenderer.drawSkeleton(this.skeleton, false);
        this.sceneRenderer.end();

        if (this.staticAnimationName) {
          return;
        }
      }

      this.rafId = window.requestAnimationFrame(() => this.renderFrame());
    }
  }
};
